/**
 * GenerationFormPromptSection
 *
 * Main generation settings: ratio, template, brand, resolution, prompt, delivery, image count.
 */
import { useState } from 'react';
import type { UseFormRegister, UseFormWatch, UseFormSetValue } from 'react-hook-form';
import { ChevronDown, ChevronRight, Settings } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Select, Textarea, Combobox } from '@/components/v2/ui';
import { GenerationBrandSection } from './GenerationBrandSection';
import { RichTextPromptInput } from './RichTextPromptInput';

const RATIOS = [
  { value: '1:1', label: '1:1 (Quadrat)' },
  { value: '4:3', label: '4:3 (Landschaft)' },
  { value: '3:4', label: '3:4 (Portrait)' },
  { value: '16:9', label: '16:9 (Breitbild)' },
  { value: '9:16', label: '9:16 (Story)' },
];

interface FormData {
  prompt_input: string;
  ratio: string;
  resolution: string;
  preset_type: string;
  delivery_method: 'wait' | 'mail';
  image_count: string;
  image_format: string;
  filename_prefix: string;
  filename_suffix: string;
  png_quality: string;
  gen_quality: string;
}

const IMAGE_FORMATS = [
  { value: 'png', label: 'PNG (verlustfrei)' },
  { value: 'jpg', label: 'JPG (komprimiert)' },
  { value: 'webp', label: 'WebP (modern)' },
];

const PNG_QUALITY_OPTIONS = [
  { value: 'web_optimized', label: 'Web Optimiert' },
  { value: 'high', label: 'Hoch' },
  { value: 'max', label: 'Maximum' },
];

interface GenerationFormPromptSectionProps {
  register: UseFormRegister<FormData>;
  watch: UseFormWatch<FormData>;
  setValue: UseFormSetValue<FormData>;
  ratio: string;
  presetType: string;
  resolution: string;
  imageCount: string;
  presetOptions: Array<{ value: string; label: string; disabled?: boolean; imageUrl?: string | null }>;
  selectedBrandId: string | null;
  imageFormat: string;
  pngQuality: string;
  onRatioChange: (value: string) => void;
  onPresetChange: (value: string) => void;
  onResolutionChange: (value: string) => void;
  onImageCountChange: (value: string) => void;
  onBrandChange: (brandId: string | null) => void;
  onImageFormatChange: (value: string) => void;
  onPngQualityChange: (value: string) => void;
  hideBrandSelector?: boolean;
  referencedAssetIdsRef?: React.MutableRefObject<string[]>;
  /** Dynamic ratio options (overrides default RATIOS) */
  ratioOptions?: Array<{ value: string; label: string }>;
  /** Dynamic resolution options (overrides default RESOLUTIONS) */
  resolutionOptions?: Array<{ value: string; label: string }>;
  referenceSlots?: Array<{ id: string | null; previewUrl: string | null }>;
}

export function GenerationFormPromptSection({
  register,
  watch,
  setValue,
  ratio,
  presetType,
  resolution,
  imageCount,
  presetOptions,
  selectedBrandId,
  imageFormat,
  pngQuality,
  onRatioChange,
  onPresetChange,
  onResolutionChange,
  onImageCountChange,
  onBrandChange,
  onImageFormatChange,
  onPngQualityChange,
  hideBrandSelector = false,
  referencedAssetIdsRef,
  ratioOptions,
  resolutionOptions,
  referenceSlots,
}: GenerationFormPromptSectionProps) {
  const { t } = useTranslation();
  const imageCountNumber = parseInt(imageCount) || 1;
  const [showExpertSettings, setShowExpertSettings] = useState(false);
  const promptValue = watch('prompt_input') || '';
  const DEFAULT_RESOLUTIONS = [
    { value: '1k', label: '1K (1024×1024)' },
    { value: '2k', label: '2K (2048×2048)' },
    { value: '4k', label: '4K (4096×4096)' },
  ];
  const RESOLUTIONS = resolutionOptions ?? DEFAULT_RESOLUTIONS;

  return (
    <div className="space-y-4">
      {/* 1. Template mit Suche */}
      <Combobox
        label="Template"
        placeholder={t('generation.templateChoose')}
        searchPlaceholder={t('batch.templateSearch')}
        emptyText={t('batch.noTemplates')}
        options={presetOptions}
        value={presetType}
        onValueChange={onPresetChange}
      />

      {/* Brand Section - optional (nur wenn nicht hidden) */}
      {!hideBrandSelector && (
        <GenerationBrandSection
          selectedBrandId={selectedBrandId}
          onBrandChange={onBrandChange}
        />
      )}

      {/* 2. Anweisungen (Prompt) - mit inline Asset-Badges wenn Brand oder REF-Bilder verfügbar */}
      {selectedBrandId || (referenceSlots ?? []).some(s => s.id !== null) ? (
        <div>
          <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-2">
            {t('generation.promptLabel')}
          </label>
          <RichTextPromptInput
            brandId={selectedBrandId}
            value={promptValue}
            referenceSlots={referenceSlots}
            onChange={(text, assetIds) => {
              setValue('prompt_input', text);
              if (referencedAssetIdsRef) {
                referencedAssetIdsRef.current = assetIds;
              }
            }}
            placeholder={t('generation.promptPlaceholder')}
          />
          <p className="mt-2 text-xs text-neutral-500">
            💡 {t('generation.promptHint')}
          </p>
        </div>
      ) : (
        <Textarea
          label={t('generation.promptLabel')}
          placeholder={t('generation.additionalInstructionsPlaceholder')}
          rows={3}
          {...register('prompt_input')}
        />
      )}

      {/* 3. Seitenverhältnis */}
      <Select
        label={t('generation.aspectRatioLabel')}
        options={ratioOptions ?? RATIOS}
        value={ratio}
        onValueChange={onRatioChange}
      />

      {/* 4. Auflösung */}
      <Select
        label={t('generation.resolutionLabel')}
        options={RESOLUTIONS}
        value={resolution}
        onValueChange={onResolutionChange}
        helperText={t('generation.resolutionHint')}
      />

      {/* 5. Anzahl Bilder - Slider */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <label className="text-sm font-medium text-neutral-700 dark:text-neutral-300">
            Anzahl Bilder
          </label>
          <span className="text-sm font-semibold text-orange-600 dark:text-orange-400">
            {imageCountNumber}
          </span>
        </div>
        <input
          type="range"
          min="1"
          max="4"
          step="1"
          value={imageCountNumber}
          onChange={(e) => onImageCountChange(e.target.value)}
          className="w-full h-2 bg-neutral-200 dark:bg-neutral-700 rounded-lg appearance-none cursor-pointer accent-orange-500"
        />
        <p className="text-xs text-neutral-500">
          Generiere bis zu 4 Variationen parallel
        </p>
      </div>

      {/* 6. Experteneinstellungen - Collapsible */}
      <div className="border-t border-neutral-200 dark:border-neutral-800 pt-4">
        <button
          type="button"
          onClick={() => setShowExpertSettings(!showExpertSettings)}
          className="flex items-center justify-between w-full text-left text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors"
        >
          <div className="flex items-center gap-2">
            <Settings className="h-4 w-4" />
            <span>{t('common.settings')}</span>
          </div>
          {showExpertSettings ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronRight className="h-4 w-4" />
          )}
        </button>

        {showExpertSettings && (
          <div className="mt-4 space-y-4">
            {/* Bildformat */}
            <Select
              label="Bildformat"
              options={IMAGE_FORMATS}
              value={imageFormat}
              onValueChange={onImageFormatChange}
            />

            {/* PNG Qualität - nur wenn PNG ausgewählt */}
            {imageFormat === 'png' && (
              <Select
                label={t('generation.pngQuality')}
                options={PNG_QUALITY_OPTIONS}
                value={pngQuality}
                onValueChange={onPngQualityChange}
              />
            )}

            {/* Dateiname Prefix */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Dateiname Prefix
              </label>
              <input
                type="text"
                placeholder="z.B. produkt_"
                {...register('filename_prefix')}
                className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>

            {/* Dateiname Suffix */}
            <div>
              <label className="block text-sm font-medium text-neutral-700 dark:text-neutral-300 mb-1">
                Dateiname Suffix
              </label>
              <input
                type="text"
                placeholder="z.B. _final"
                {...register('filename_suffix')}
                className="w-full px-3 py-2 text-sm border border-neutral-300 dark:border-neutral-700 rounded-lg bg-white dark:bg-neutral-800 text-neutral-900 dark:text-neutral-100 focus:outline-none focus:ring-2 focus:ring-orange-500"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
